import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ADSENSE_CLIENT, ADSENSE_ENABLED } from "../appConfig";

const AdConsentContext = createContext({
  canServeAds: false,
  isAdScriptReady: false
});

export function AdConsentProvider({ children }) {
  const [isAdScriptReady, setIsAdScriptReady] = useState(false);

  useEffect(() => {
    if (!ADSENSE_ENABLED || !ADSENSE_CLIENT) {
      setIsAdScriptReady(false);
      return;
    }

    const existingScript = document.querySelector(`script[data-adsense-client="${ADSENSE_CLIENT}"]`);

    if (existingScript) {
      if (window.adsbygoogle) {
        setIsAdScriptReady(true);
        return;
      }

      const handleLoad = () => setIsAdScriptReady(true);
      const handleError = () => setIsAdScriptReady(false);
      existingScript.addEventListener("load", handleLoad);
      existingScript.addEventListener("error", handleError);

      return () => {
        existingScript.removeEventListener("load", handleLoad);
        existingScript.removeEventListener("error", handleError);
      };
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    script.crossOrigin = "anonymous";
    script.dataset.adsenseClient = ADSENSE_CLIENT;
    script.onload = () => setIsAdScriptReady(true);
    script.onerror = () => setIsAdScriptReady(false);
    document.head.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  const value = useMemo(() => ({
    canServeAds: ADSENSE_ENABLED && Boolean(ADSENSE_CLIENT),
    isAdScriptReady
  }), [isAdScriptReady]);

  return <AdConsentContext.Provider value={value}>{children}</AdConsentContext.Provider>;
}

export function useAdConsent() {
  return useContext(AdConsentContext);
}